import React from 'react';
import { DefaultButton, IconButton, Stack, TextField, TooltipHost, Icon } from '@fluentui/react';
import TokenAutosuggestField from '../Common/TokenAutosuggestField';
import Setting from '../Common/Setting';
import WebhookTokenReference from './WebhookTokenReference';
import { useWebhookTokens } from './useWebhookTokens';
import {
    IHeaderRow,
    MAX_HEADERS,
    newHeaderRow,
    validateHeaderRows,
} from './webhookHeaders';

// Editor for a webhook's custom HTTP headers. Row shape, validation and payload conversion live in
// webhookHeaders.ts.

const captionStyle: React.CSSProperties = { fontSize: 12, color: '#605e5c' };
const errorStyle: React.CSSProperties = { fontSize: 12, color: '#a4262c' };

const HEADERS_TOOLTIP = 'Sent with every call of this webhook. Values can be plain text, tokens, or a mix. '
    + 'Mark a header Secret to hide its value after saving.';

interface IWebhookHeadersEditorProps {
    type?: string;
    rows: IHeaderRow[];
    onChange: (rows: IHeaderRow[]) => void;
}

const WebhookHeadersEditor: React.FC<IWebhookHeadersEditorProps> = ({ type, rows, onChange }) => {
    const tokens = useWebhookTokens(type);
    const errors = validateHeaderRows(rows);

    const updateRow = (key: string, patch: Partial<IHeaderRow>) =>
        onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));

    // Turning Secret on over a typed value just masks it. Turning it off over a saved secret has no
    // plain value to reveal (the server never sent it), so the field starts empty; the caption below
    // warns that saving replaces the stored value. hasValue and originalName are kept so toggling back
    // on before saving still resolves to "keep the stored secret".
    const toggleSecret = (row: IHeaderRow) => {
        const turningOff = !!row.secret;
        updateRow(row.key, { secret: !turningOff, value: turningOff && row.hasValue ? '' : row.value });
    };

    const renderValue = (row: IHeaderRow) => {
        if (row.secret) {
            return (
                <TextField
                    type='password'
                    canRevealPassword
                    ariaLabel='Header value'
                    placeholder={row.hasValue ? 'Saved. Leave blank to keep the current value.' : 'Value'}
                    value={row.value ?? ''}
                    onChange={(_, v) => updateRow(row.key, { value: v ?? '' })}
                    autoComplete='off'
                />
            );
        }
        if (tokens.supported) {
            return (
                <TokenAutosuggestField
                    placeholder='Value'
                    suggestions={tokens.suggestions}
                    value={row.value ?? ''}
                    onChange={(v) => updateRow(row.key, { value: v ?? '' })}
                />
            );
        }
        return (
            <TextField
                ariaLabel='Header value'
                placeholder='Value'
                value={row.value ?? ''}
                onChange={(_, v) => updateRow(row.key, { value: v ?? '' })}
            />
        );
    };

    return (
        <Setting id='webhooks.details.headers'>
            <Stack tokens={{ childrenGap: '0.5em' }}>
                <Stack horizontal verticalAlign='center' tokens={{ childrenGap: '0.25em' }}>
                    <strong>Custom Headers</strong>
                    <TooltipHost content={HEADERS_TOOLTIP}>
                        <Icon iconName='Info' aria-label={HEADERS_TOOLTIP} style={{ cursor: 'help' }} />
                    </TooltipHost>
                    {tokens.supported && <WebhookTokenReference type={type} />}
                </Stack>
                {type && !tokens.supported && (
                    <div style={captionStyle}>
                        This webhook type does not substitute tokens, so header values are sent as typed.
                    </div>
                )}
                {rows.map((row, i) => (
                    <div key={row.key}>
                        <Stack horizontal verticalAlign='start' tokens={{ childrenGap: '0.5em' }}>
                            <Stack.Item styles={{ root: { width: '34%' } }}>
                                <TextField
                                    ariaLabel='Header name'
                                    placeholder='Name'
                                    value={row.name ?? ''}
                                    onChange={(_, v) => updateRow(row.key, { name: v ?? '' })}
                                />
                            </Stack.Item>
                            <Stack.Item grow styles={{ root: { minWidth: 0 } }}>{renderValue(row)}</Stack.Item>
                            <IconButton
                                iconProps={{ iconName: row.secret ? 'Lock' : 'Unlock' }}
                                title={row.secret ? 'Secret: value is hidden after saving. Click to make it plain.' : 'Not secret. Click to hide the value after saving.'}
                                ariaLabel='Secret'
                                checked={!!row.secret}
                                onClick={() => toggleSecret(row)}
                            />
                            <IconButton
                                iconProps={{ iconName: 'Delete' }}
                                title='Remove header'
                                ariaLabel='Remove header'
                                onClick={() => onChange(rows.filter((r) => r.key !== row.key))}
                            />
                        </Stack>
                        {errors[i] && <div style={errorStyle} role='alert'>{errors[i]}</div>}
                        {!row.secret && row.hasValue && (
                            <div style={captionStyle}>The saved secret value will be replaced when you save.</div>
                        )}
                    </div>
                ))}
                <div>
                    <DefaultButton
                        iconProps={{ iconName: 'Add' }}
                        text='Add header'
                        disabled={rows.length >= MAX_HEADERS}
                        onClick={() => onChange([...rows, newHeaderRow()])}
                    />
                    {rows.length >= MAX_HEADERS && (
                        <span style={{ ...captionStyle, marginLeft: 8 }}>Up to {MAX_HEADERS} headers.</span>
                    )}
                </div>
            </Stack>
        </Setting>
    );
};

export default WebhookHeadersEditor;
